import cx from 'classnames';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {TestAttribute, WithTestAttributeProps} from 'components';
import {Icon, IconColor, IconName} from 'components/UIKit/Icon';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

const T_KEYS = TRANSLATION_KEYS.UI_KIT.BUTTON;

type Width = 'auto' | 'small' | 'normal' | 'large' | 'full';
const widthMap: {[key in Width]: string} = {
    auto: '',
    small: 'tw-min-w-31',
    normal: 'tw-min-w-40',
    large: 'tw-min-w-49',
    full: 'tw-w-full',
};

type Height = 'auto' | 'normal' | 'large' | 'full';
const heightMap: {[key in Height]: string} = {
    auto: '',
    normal: 'tw-min-h-8',
    large: 'tw-min-h-16',
    full: 'tw-h-full',
};

type Color = 'default' | 'primary' | 'attention' | 'unstyled' | 'alternative' | 'success' | 'primary-dimmed';
const colorClassMap: {[key in Color]: string} = {
    default: 'tw-text-dark tw-border-default tw-bg-transparent hover:tw-border-dark',
    primary: 'tw-bg-primary tw-text-alternative tw-border-primary hover:tw-bg-primary-light hover:tw-border-primary-light',
    'primary-dimmed':
        'tw-bg-primary-dimmed tw-text-alternative tw-border-primary-dimmed hover:tw-bg-primary-light hover:tw-border-primary-light',
    attention: 'tw-bg-alert tw-text-alternative tw-border-alert hover:tw-bg-alert-light hover:tw-border-alert-light',
    success: 'tw-bg-success tw-text-alternative tw-border-success hover:tw-bg-success-light hover:tw-border-success-light',
    alternative: 'tw-border-light hover:tw-bg-primary-light hover:tw-bg-opacity-20',
    unstyled: '',
};

const disabledColorClassMap: {[key in Color]: string} = {
    default: '',
    unstyled: '',
    alternative: '',
    primary: 'tw-bg-default tw-border-default',
    'primary-dimmed': 'tw-bg-default tw-border-default',
    attention: 'tw-bg-default tw-border-default',
    success: 'tw-bg-default tw-border-default',
};

const loadingTextClassMap: {[key in Color]: string} = {
    default: 'tw-text-default',
    unstyled: 'tw-text-default',
    primary: 'tw-text-alternative',
    'primary-dimmed': 'tw-text-alternative',
    attention: 'tw-text-default',
    alternative: 'tw-text-alternative',
    success: 'tw-text-alternative',
};

type FontStyle = 'default' | 'secondary' | 'normal';
const fontStyleMap: {[key in FontStyle]: string} = {
    default: 'tw-font-bold',
    secondary: 'tw-font-secondary',
    normal: 'tw-font-normal',
};

type FontSize = 'normal' | 'large';
const fontSizeMap: {[key in FontSize]: string} = {
    normal: 'tw-ts-base',
    large: 'tw-ts-h3'
};

type Props = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & WithTestAttributeProps & {
    color?: Color;
    width?: Width;
    height?: Height;
    loading?: boolean;
    rounded?: boolean;
    fontStyle?: FontStyle;
    fontSize?: FontSize;
    icon?: IconName;
    iconColor?: IconColor;
    Icon?: React.ReactNode;
    children?: React.ReactNode;
    animated?: boolean;
};

export type ButtonProps = Props;

export const Button: React.FC<Props> = ({
    className,
    type = 'button',
    color = 'default',
    width = 'normal',
    height = 'normal',
    fontStyle = 'default',
    fontSize = 'normal',
    disabled: disabledProp = false,
    loading = false,
    rounded = false,
    animated = false,
    icon,
    iconColor: iconColorProp,
    Icon: IconComponent,
    testAttribute,
    onClick,
    children,
    'aria-expanded': ariaExpanded
}: Readonly<Props>) => {
    const {t} = useTranslation();

    const disabled = useMemo(() => disabledProp || loading, [disabledProp, loading]);
    const colors = useMemo(() => disabled ? disabledColorClassMap[color] : colorClassMap[color], [color, disabled]);

    const iconColor = useMemo(() => {
        if (iconColorProp && !disabled) {
            return iconColorProp;
        }
        if (disabled) {
            return 'default-dimmed';
        } else if (color === 'default') {
            return 'default';
        } else {
            return 'alternative';
        }
    }, [disabled, color, iconColorProp]);

    return (
        <TestAttribute testAttribute={testAttribute}>
            <button
                className={cx(
                    'tw-relative tw-inline-block tw-no-underline tw-select-none',
                    'tw-border focus:tw-outline-none tw-p-1.5',
                    colors, widthMap[width], heightMap[height], fontStyleMap[fontStyle], fontSizeMap[fontSize],
                    {
                        'tw-rounded-full': rounded,
                        'tw-animate-pulse': animated,
                        'tw-text-default-dimmed tw-pointer-events-none': disabled,
                        'tw-bg-primary-dimmed': color === 'primary' && disabled && loading,
                    }, className)}
                type={type}
                disabled={disabled || loading}
                aria-expanded={ariaExpanded}
                onClick={onClick}
            >
                {loading && <span className={loadingTextClassMap[color]}>{t(T_KEYS.LOADING)}</span>}
                <span className={cx('tw-flex-center tw-gap-2', {'tw-hidden': loading})}>
                    {IconComponent && IconComponent}
                    {icon && <Icon name={icon} color={iconColor} size='sm' />}
                    <span className='tw-text-no-wrap'>{children}</span>
                </span>
            </button>
        </TestAttribute>
    );
};
